import { NavLink } from 'react-router-dom';
import { Users, Tag, Radio, Layers, Palette, Settings, Eye, LayoutDashboard, Pencil, Smartphone, Sun, Moon, BarChart3, Tv, Type, Home, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { useTheme } from '@/hooks/useTheme';
import { Avatar } from '@/components/ui/Avatar';
import { RoleChip } from '@/components/ui/RoleChip';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import { ProfileEditDialog } from '@/components/auth/ProfileEditDialog';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** If true, only visible to staff (admin/editor/reviewer). */
  staffOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/',           label: 'Console',     icon: LayoutDashboard },
  { to: '/tapes',      label: 'Tapes',       icon: Tag },
  { to: '/mixtapes',   label: 'Mixtapes',    icon: Layers },
  { to: '/watch',      label: 'Watch',       icon: Tv },
  { to: '/portada',    label: 'Portada',     icon: Home,   staffOnly: true },
  { to: '/search',     label: 'Search',      icon: Search, staffOnly: true },
  { to: '/broadcasts', label: 'Broadcasts',  icon: Radio },
  { to: '/genres',     label: 'Genres',      icon: Palette },
  { to: '/users',      label: 'People',      icon: Users },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/preview',   label: 'Feed preview', icon: Smartphone, staffOnly: true },
  { to: '/analytics', label: 'Analytics',    icon: BarChart3,  staffOnly: true },
  { to: '/fonts',     label: 'Fonts',        icon: Type,       staffOnly: true },
  { to: '/ops',       label: 'Ops',          icon: Settings,   staffOnly: true },
  { to: '/audit',     label: 'Audit log',    icon: Eye,        staffOnly: true },
];

export function Sidebar() {
  const { profile, role, isStaff } = useSession();
  const { theme, toggle: toggleTheme } = useTheme();
  const primaryVisible   = NAV.filter((item) => !item.staffOnly || isStaff);
  const secondaryVisible = SECONDARY_NAV.filter((item) => !item.staffOnly || isStaff);
  return (
    <aside className="flex h-screen w-60 flex-col border-r border-border bg-bg-card">
      {/* Brand */}
      <div className="flex items-center gap-2 border-b border-border px-5 py-4">
        <img src="/logo.png" alt="" className="h-7 w-7 object-contain" />
        <div className="flex-1 min-w-0">
          <div className="font-editorial text-h3 leading-none tracking-tight">TAPES</div>
          <BuildStamp />
        </div>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 px-2 py-4">
        <ul className="space-y-1">
          {primaryVisible.map((item) => (
            <li key={item.to}><NavLinkItem item={item} /></li>
          ))}
        </ul>
        {secondaryVisible.length > 0 ? (
          <>
            <div className="mt-6 px-3 text-[10px] uppercase tracking-widest text-fg-faint">Operations</div>
            <ul className="mt-2 space-y-1">
              {secondaryVisible.map((item) => (
                <li key={item.to}><NavLinkItem item={item} /></li>
              ))}
            </ul>
          </>
        ) : null}
      </nav>

      {/* User card */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 mb-3 group">
          <Avatar src={profile?.avatar_url} size={36} />
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate text-fg">{profile?.display_name ?? '/'}</div>
            <div className="text-xs font-mono text-fg-faint truncate">@{profile?.handle ?? '/'}</div>
          </div>
          <ProfileEditDialog trigger={
            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          } />
        </div>
        <div className="flex items-center justify-between gap-2">
          {role ? <RoleChip role={role} /> : <span className="text-xs text-fg-faint">no role</span>}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => supabase.auth.signOut()}>Sign out</Button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function BuildStamp() {
  // Build-time constants from vite.config.ts → define. Useful to confirm a
  // hard refresh picked up the latest bundle: the HH:mm timestamp updates
  // on every deploy.
  const buildDate = new Date(__BUILD_TIME__);
  const hhmm = buildDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <div
      className="text-[10px] font-mono text-fg-faint mt-0.5 leading-none truncate"
      title={`Build: ${buildDate.toLocaleString()}`}
    >
      v{__APP_VERSION__} · {hhmm}
    </div>
  );
}

function NavLinkItem({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-bg-elevated text-fg font-medium'
            : 'text-fg-muted hover:bg-bg-elevated/60 hover:text-fg',
        )
      }
    >
      <item.icon className="h-4 w-4 flex-shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  );
}
